import type {
  FlatLot,
  Watchlist,
  WatchlistComparison,
} from "@foundation/watchlist-client";
import type {
  PortfolioChartDateRange,
  ComparisonChartWatchlist,
} from "~types/portfolio";
import serializeSearchParams from "~utils/serializeSearchParams";
import type {
  TickerChartLegendData,
  WatchlistComparisonChartData,
} from "~types/types";
import { NO_COMPARISON_TICKER } from "~constants/Portfolio";

/* eslint-disable @typescript-eslint/no-var-requires */
const {
  preset: marketsPreset,
} = require("@bloomberg/media-ui-markets/markets-preset");
/* eslint-enable @typescript-eslint/no-var-requires */

const CHART_COLORS = marketsPreset.theme.extend.colors;

const CHART_COLOR_BY_INDEX = [
  "#000",
  CHART_COLORS["chart-blue"],
  CHART_COLORS["chart-orange"],
  CHART_COLORS["chart-purple"],
  CHART_COLORS["chart-sky-blue"],
  CHART_COLORS["chart-red"],
  CHART_COLORS["chart-mustard"],
  CHART_COLORS["chart-pink"],
  CHART_COLORS["chart-green"],
  CHART_COLORS["chart-brown"],
];

export const DEFAULT_COMPARISON_TICKERS = [
  "INDU:IND",
  "SPX:IND",
  "CCMP:IND",
  "B500:IND",
];

export const getChartTickerColor = (tickerPosition: number) => {
  if (tickerPosition >= CHART_COLOR_BY_INDEX.length) {
    return CHART_COLORS[0];
  }

  return CHART_COLOR_BY_INDEX[tickerPosition];
};

async function fetchComparisonChartData(
  watchlist: ComparisonChartWatchlist,
  chartType: PortfolioChartDateRange,
  compareWatchlists?: Array<ComparisonChartWatchlist>,
  compareTickers?: Array<string>,
  isPerformanceChart?: boolean
) {
  const searchParams = serializeSearchParams({
    watchlistId: watchlist.watchlistId,
    chartType,
  });
  const compareAPI = `/bazaar/api/watchlist/comparisonChart${decodeURIComponent(
    searchParams
  )}`;

  const response = await fetch(compareAPI, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      watchlist,
      chartType,
      compareWatchlists,
      compareTickers,
      isPerformanceChart,
    }),
  });

  if (!response.ok) return null;

  return response.json();
}

export function normalizeComparison(comparisons: Array<WatchlistComparison>) {
  return (
    comparisons?.filter((comparison) => {
      // ignore no comparison
      if (comparison.comparisonTicker === NO_COMPARISON_TICKER) {
        return false;
      }

      // ignore empty watchlist
      if (
        comparison.comparisonWatchlistId &&
        !comparison.comparisonWatchlistLots?.length
      ) {
        return false;
      }

      return true;
    }) || []
  );
}

function isInvalidChartData(
  response: Array<WatchlistComparisonChartData>,
  watchlistComparison: WatchlistComparison[]
) {
  // chart data is not available
  if (!response?.length) {
    return true;
  }

  // comparisons are available, but chart data for comparisons missing
  if (response.length <= watchlistComparison.length) {
    return true;
  }

  return response.some((chartData: WatchlistComparisonChartData) => {
    return !chartData.priceMovements?.length;
  });
}

export async function fetchWatchlistComparisonChartData(
  chartType: PortfolioChartDateRange,
  currentWatchlist: Watchlist,
  watchlistComparisons: WatchlistComparison[],
  isPerformanceChart = false
): Promise<Array<WatchlistComparisonChartData>> {
  const watchlistIds = new Set<string>();
  const watchlistFlatLots: FlatLot[] = [];
  const comparisons = normalizeComparison(watchlistComparisons);

  // prepare flat lot object for current watchlist
  currentWatchlist.positions?.forEach((position) => {
    position.lots.forEach((lot) => {
      watchlistFlatLots.push({
        lotId: lot.lotId,
        ticker: lot.ticker,
        shares: lot.shares.number,
        buyPrice: lot.shares.buyprice,
        buyDate: lot.shares.buydate as number,
      });
    });
  });

  // prepare current watchlist
  const chartWatchlist: ComparisonChartWatchlist = {
    watchlistId: currentWatchlist.portInfo.id,
    watchlistName: currentWatchlist.portInfo.name,
    issuedCurrency: currentWatchlist.portInfo.currency,
    lots: watchlistFlatLots,
  };

  // user selected comparison tickers
  const comparisonTickers = comparisons
    .filter((item) => Boolean(item.comparisonTicker))
    .map((item) => item.comparisonTicker) as string[];

  // user selected comparison watchlists
  const comparisonWatchlists: ComparisonChartWatchlist[] = comparisons
    .filter((item) => Boolean(item.comparisonWatchlistId))
    .map((item) => ({
      watchlistId: item.comparisonWatchlistId as number,
      watchlistName: item.comparisonWatchlistName as string,
      issuedCurrency: item.comparisonWatchlistCurrency as string,
      lots: item.comparisonWatchlistLots as FlatLot[],
    }));

  const result: Array<WatchlistComparisonChartData> =
    await fetchComparisonChartData(
      chartWatchlist,
      chartType,
      comparisonWatchlists,
      comparisonTickers,
      isPerformanceChart
    );

  // validate if chart data for watchlist and comparisons are available
  const dataIsInvalid = isInvalidChartData(result, comparisons);

  // return empty response when data is invalid
  if (dataIsInvalid) {
    return [];
  }

  // add current watchlist id
  watchlistIds.add(currentWatchlist.portInfo.id.toString());

  // add comparison watchlists id
  comparisonWatchlists.forEach((comparison) => {
    watchlistIds.add(comparison.watchlistId.toString());
  });

  return result?.map((item, index) => ({
    ...item,
    name: watchlistIds.has(item.id.toString()) ? item.name : item.id.toString(),
    color: getChartTickerColor(index),
  }));
}

export function isInValidIntradayData(
  watchlistId: number,
  selectedTab: string,
  chartDataList: Array<WatchlistComparisonChartData>,
  watchlistComparison: WatchlistComparison[]
) {
  if (selectedTab !== "1D") {
    return false;
  }

  const watchlistIdSet = new Set<number>();

  // prepare a lit of all watchlist id
  watchlistComparison.forEach((comparison) => {
    if (comparison.comparisonWatchlistId) {
      watchlistIdSet.add(comparison.comparisonWatchlistId);
    }
  });

  const watchlistChartData = chartDataList.find(
    (chartData) => chartData.id === watchlistId
  );

  if (!watchlistChartData) {
    return false;
  }

  // show overlay when watchlist doesn't have any US tickers
  // server computes this while flattening the watchlist data
  return watchlistChartData.exchangeCode !== "US";
}

export const filterSelectedTickers = (
  tickerChartLegends: TickerChartLegendData[],
  chartDataList: Array<WatchlistComparisonChartData>
): Array<WatchlistComparisonChartData> => {
  // skip filtering when legend selection is not available
  if (!tickerChartLegends?.length || !chartDataList?.length) {
    return chartDataList || [];
  }

  const selectedTickers = new Map<string | number, string>();

  // prepare a map of selected tickers
  tickerChartLegends.forEach((legend) => {
    if (legend.checked) {
      selectedTickers.set(legend.id, legend.color);
    }
  });

  // filter selected tickers from chart data
  let filteredChartData =
    chartDataList?.filter((chartData) =>
      selectedTickers.has(chartData.id.toString())
    ) || [];

  // render comparison when multiple tickers are selected
  if (filteredChartData.length > 1) {
    filteredChartData = filteredChartData.map((chartData) => ({
      ...chartData,
      priceMovements: chartData.priceMovements.map((priceMovement) => ({
        ...priceMovement,
        value: priceMovement.normalizedValue || priceMovement.value,
      })),
    }));
  }

  return filteredChartData.map((chartData) => ({
    ...chartData,
    color: selectedTickers.get(chartData.id.toString()) as string,
  }));
};

const LARGE_DESKTOP_CHART_MAXIMIZED_WIDTH = 1010;
const LARGE_DESKTOP_CHART_MAXIMIZED_HEIGHT = 578;
const LARGE_DESKTOP_CHART_MINIMIZED_WIDTH = 402;
const LARGE_DESKTOP_CHART_MINIMIZED_HEIGHT = 200;
const SMALL_DESKTOP_CHART_MAXIMIZED_WIDTH = 797;
const SMALL_DESKTOP_CHART_MAXIMIZED_HEIGHT = 456;
const SMALL_DESKTOP_CHART_MINIMIZED_WIDTH = 402;
const SMALL_DESKTOP_CHART_MINIMIZED_HEIGHT = 200;
const TABLET_CHART_MAXIMIZED_WIDTH = 617;
const TABLET_CHART_MAXIMIZED_HEIGHT = 299;
const TABLET_CHART_MINIMIZED_WIDTH = 617;
const TABLET_CHART_MINIMIZED_HEIGHT = 299;
const DEFAULT_CHART_WIDTH = 500;
const DEFAULT_CHART_HEIGHT = 300;

// a function which takes the breakpoint as input and returns the
// width and height of the chart as output
export function getChartDimensions(
  tablet: boolean | null,
  extraSmallDesktop: boolean | null,
  smallDesktop: boolean | null,
  largeDesktop: boolean | null,
  isMaximized: boolean
) {
  if (isMaximized) {
    if (largeDesktop) {
      return {
        chartWidth: LARGE_DESKTOP_CHART_MAXIMIZED_WIDTH,
        chartHeight: LARGE_DESKTOP_CHART_MAXIMIZED_HEIGHT,
      };
    }
    if (smallDesktop) {
      return {
        chartWidth: SMALL_DESKTOP_CHART_MAXIMIZED_WIDTH,
        chartHeight: SMALL_DESKTOP_CHART_MAXIMIZED_HEIGHT,
      };
    }
    if (tablet) {
      return {
        chartWidth: TABLET_CHART_MAXIMIZED_WIDTH,
        chartHeight: TABLET_CHART_MAXIMIZED_HEIGHT,
      };
    }

    if (extraSmallDesktop) {
      return {
        chartWidth: TABLET_CHART_MAXIMIZED_WIDTH,
        chartHeight: TABLET_CHART_MAXIMIZED_HEIGHT,
      };
    }

    return {
      chartWidth: DEFAULT_CHART_WIDTH,
      chartHeight: DEFAULT_CHART_HEIGHT,
    };
  }

  if (largeDesktop) {
    return {
      chartWidth: LARGE_DESKTOP_CHART_MINIMIZED_WIDTH,
      chartHeight: LARGE_DESKTOP_CHART_MINIMIZED_HEIGHT,
    };
  }

  if (smallDesktop) {
    return {
      chartWidth: SMALL_DESKTOP_CHART_MINIMIZED_WIDTH,
      chartHeight: SMALL_DESKTOP_CHART_MINIMIZED_HEIGHT,
    };
  }

  if (tablet) {
    return {
      chartWidth: TABLET_CHART_MINIMIZED_WIDTH,
      chartHeight: TABLET_CHART_MINIMIZED_HEIGHT,
    };
  }

  if (extraSmallDesktop) {
    return {
      chartWidth: TABLET_CHART_MINIMIZED_WIDTH,
      chartHeight: TABLET_CHART_MINIMIZED_HEIGHT,
    };
  }

  return {
    chartWidth: DEFAULT_CHART_WIDTH,
    chartHeight: DEFAULT_CHART_HEIGHT,
  };
}
